/**
 * @file modal-mixin.ts
 *
 * Owns the as-modal-overlay reference and modal content state. Provides three
 * modal entry points used throughout the app:
 *
 *   showModal()      — renders a transient panel (mesh transfer, autoload, etc.)
 *                      from modalContent and cleans up via the 'as-modal-closed'
 *                      listener when the overlay closes.
 *   openModal()      — opens a named, persistent panel by id/tag, creating the
 *                      element if it does not yet exist.
 *   showErrorModal() — surfaces the standing error panel.
 *
 * Exposes: modalOverlay, modalContent, showModal, openModal, showErrorModal
 */
import { AsModalOverlay } from '../../modals/as-modal-overlay.ts';
import { buildModalHTML, type ModalContent } from '../../modals/modalTemplates.ts';
import { appInfo } from '../lib/appConfig.ts';
import { devlog } from '../../../util/utils.ts';
import { sendEv } from '../../../data/events.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** How long a panel that reports work in progress waits before it appears.
 *  Work that finishes inside this never puts a modal on screen. */
export const MODAL_SHOW_DELAY_MS = 250;

/** Surface added by ModalMixin. Referenced by ErrorMixin (showErrorModal),
 *  ProjectMixin (openModal), and TransferMixin (showModal, showErrorModal). */
export interface ModalMixinMembers {
  modalOverlay: AsModalOverlay | null;
  /** Current transient modal data; type 'none' means no transient panel. */
  modalContent: ModalContent;
  /** Renders modalContent into the overlay and opens it. */
  /** Renders modalContent into the overlay and opens it. `delayMs` holds the
   *  panel back until then, and closing before it elapses shows nothing. */
  showModal(opts?: { delayMs?: number }): void;
  /** Opens a named persistent panel by id, creating it if absent. */
  openModal(modalId: string, tagName: string, attributes?: Record<string, string>): void;
  /** Surfaces the standing error panel with the given error detail. */
  showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void;
}

/** What the transfer panel needs to render. Supplied by TransferMixin, which
 *  sits further out in the chain than this mixin, so it cannot be a constraint
 *  on the base and is read off `this` instead. */
interface TransferPanelInputs {
  appId: string;
  otherAppsForTransfer: string[];
  openTransferInNewTab: boolean;
}

export const ModalMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class ModalMixin extends Base {
    modalOverlay: AsModalOverlay | null = null;
    modalContent: ModalContent = { type: 'none' };

    _handleModalClosed = (): void => {
      this._onModalClosed();
      this.modalOverlay?.removeEventListener('as-modal-closed', this._handleModalClosed);
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      this.modalOverlay = (this.shadowRoot?.getElementById('as-app-overlay') ?? null) as AsModalOverlay | null;
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
    }

    // ------------------------------------------------------------------
    // Transient modal (mesh transfer etc.)
    // ------------------------------------------------------------------

    /** Renders modalContent into the overlay, opens it, and arms the close
     *  cleanup. The same arrow reference is used to add and remove the
     *  listener, so repeated opens do not stack handlers. */
    public showModal(opts: { delayMs?: number } = {}): void {
      if (this.modalContent.type === 'none') return;
      devlog('Showing modal with type:', this.modalContent.type);

      const overlay = this.modalOverlay;
      if (!overlay) { console.error('Could not find as-app-overlay in shadow DOM'); return; }

      const { appId, otherAppsForTransfer, openTransferInNewTab } = this as unknown as TransferPanelInputs;
      const panelId = `modal-panel-${this.modalContent.type}`;
      const html = buildModalHTML(this.modalContent, otherAppsForTransfer, appInfo, openTransferInNewTab, appId);

      // Only the transient panel is rebuilt per open. Everything else in the
      // overlay stays where it is: the standing error panel, the named panels,
      // and any panel a plugin registered, which nothing would put back.
      // _syncVisibility() hides them all behind the one named below.
      overlay.querySelectorAll('[id^="modal-panel-"]').forEach(el => el.remove());
      overlay.insertAdjacentHTML('beforeend', html);

      if (!overlay.querySelector('#main-error-panel')) {
        const errorPanel = document.createElement('as-error-info');
        errorPanel.id = 'main-error-panel';
        overlay.appendChild(errorPanel);
      }

      overlay.open(panelId, opts);

      this.modalOverlay?.addEventListener('as-modal-closed', this._handleModalClosed);
    }

    /** Drops the transient panel once the overlay has finished fading out. The
     *  dialog keeps painting all through its fade, so doing this any sooner
     *  changes what is on screen on the way out. Nothing takes the panel's
     *  place: visibleChildId is null while the overlay is closed either way,
     *  and both ways of showing the error panel name it themselves. */
    _onModalClosed(): void {
      this.modalContent = { type: 'none' };
      const overlay = this.modalOverlay;
      if (!overlay) return;
      overlay.whenHidden().then(() => {
        if (overlay.hasAttribute('show')) return;   // opened again mid-fade
        overlay.querySelectorAll('[id^="modal-panel-"]').forEach(el => el.remove());
        overlay.removeAttribute('visible-child');
      });
    }

    // ------------------------------------------------------------------
    // Named overlay panels
    // ------------------------------------------------------------------

    public openModal(modalId: string, tagName: string, attributes?: Record<string, string>): void {
      const overlay = this.modalOverlay;
      if (!overlay) return;
      let el = overlay.querySelector(`#${modalId}`) as HTMLElement | null;
      if (!el) {
        el = document.createElement(tagName);
        el.id = modalId;
        overlay.appendChild(el);
      }
      if (attributes) Object.entries(attributes).forEach(([k, v]) => el!.setAttribute(k, v));
      overlay.setAttribute('visible-child', modalId);
      overlay.open(modalId);
    }

    public showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void {
      const overlay = this.modalOverlay;
      if (!overlay) return;
      const panel = overlay.querySelector('#main-error-panel');
      // as-error-info only fetches system info (incl. sessionId) once at mount —
      // refresh it here so the id shown reflects the session at error time, not
      // whatever was live when the page first loaded. handleSystemInfoReq is a
      // window listener keyed off detail.requester, so this must dispatch on window.
      if (panel) sendEv(window, 'as-intrn-get-system-info', { detail: { requester: panel } });
      sendEv(panel, 'as-intrn-new-error', { bubbles: false, detail });
      overlay.setAttribute('visible-child', 'main-error-panel');
      overlay.open('main-error-panel');
    }
  };
